import React from 'react';
import DefaultLayout from '../../../layouts/DefaultLayout';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../../layouts/TemplateColumn';
import { PageHeader, PageSection, RelatedContent, CrossLink, IndicationPageIntro, AnchorLinks, TextBlock } from '../../../components';
import GlobalSafetyData, { globalSafetyBlock } from './_global_safety_data'
import { jobCodes } from '../../../util/data/jobCodes';

// SEO INFORMATION 
const pageMeta = {
    title: `KEYNOTE-811 - Adverse Reactions & Safety Data`,
    keywords: `keywords`,
    description: `Health care professionals may review selected adverse reactions reported in the KEYNOTE-811 clinical trial in patients with advanced HER2-positive gastric or GEJ adenocarcinoma.`,
    schemaJsonLD: [
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/safety/adverse-reactions/her2-positive-gastric-adenocarcinoma-keynote-811/","@type":"MedicalAudience","audienceType":"US Health Care Professionals","geographicArea":"This site is intended only for residents of the United States and its territories, including Puerto Rico."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/safety/adverse-reactions/her2-positive-gastric-adenocarcinoma-keynote-811/","@type":"MedicalWebPage","name":"Safety Profile in KEYNOTE⁠-⁠811","description":"The safety of KEYTRUDA was evaluated in 696 patients with HER2-positive gastric or GEJ cancer enrolled in KEYNOTE-811, which included 350 patients treated with KEYTRUDA 200 mg, trastuzumab, and CAPOX (n=297) or FP (n=53) every 3 weeks, compared to 346 patients treated with placebo, trastuzumab, and CAPOX (n=298) or FP (n=48) every 3 weeks."}`
    ]
}

//from /src/util/data/indications.js
// UPDATE THIS TO THE CORRECT INDICATION #
const indicationId = 39;
const jobCode = jobCodes[40].jobCode;

const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support'
        }
    }
];

const crossLinkContent = {
    sections : [
        {
            heading: "Related Information",
            items: [
                { text: 'Treatment Monitoring & Management', url: '/safety/monitoring-managing-adverse-reactions/' },
                { text: 'Dose Modifications', url: '/dosing/dose-modifications/' },
                ]
        },
    ],
    interestLinks : {
        heading: "You Might Be Interested In",
        items: [
            { eyebrow: 'Resources', text: 'Mechanism of Action', url: '/resources/mechanism-of-action/' },
        ],
    }
};

const anchorLinkModelData = [
    {label: 'Incidence of Selected Immune-Mediated Adverse Reactions'},
    {label: 'Management of Selected Immune-Mediated Adverse Reactions'},
    {label: 'Safety Profile in KEYNOTE&#8288;-&#8288;811'}
]

const Page = ({ location }) => {

    return (
        <DefaultLayout indicationId={indicationId} pageMeta={ pageMeta } location={ location } jobCode={jobCode}>
            <div data-template-name="template-e">
                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader title="Selected Adverse Reactions" isBranding/>
                   <PageSection bgColor="white">
                        <IndicationPageIntro indicationId={indicationId}/>
                   </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}

                <TemplateColumnWithSidebarContainer>
                {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main">
                        <PageSection bgColor="cloud" title="Severe and Fatal Immune-Mediated Adverse Reactions">
                            <TextBlock>{globalSafetyBlock}</TextBlock>
                            <AnchorLinks items={anchorLinkModelData} />
                        </PageSection>

                        <GlobalSafetyData />

                        <PageSection title={anchorLinkModelData[2].label}>
                            <TextBlock>
                                <p>The safety of KEYTRUDA was evaluated in 696 patients with HER2-positive gastric or GEJ cancer enrolled in KEYNOTE-811, which included 350 patients treated with KEYTRUDA 200 mg, trastuzumab, and CAPOX (n=297) or FP (n=53) every 3 weeks, compared to 346 patients treated with placebo, trastuzumab, and CAPOX (n=298) or FP (n=48) every 3 weeks.</p>   
                                <p>The median duration of exposure to KEYTRUDA was 9.2 months (range: 1 day to 33.6 months).</p>                           
                                <p>In KEYNOTE-811, fatal adverse reactions occurred in 3 patients who received KEYTRUDA in combination with trastuzumab and CAPOX (capecitabine plus oxaliplatin) or FP (5-FU plus cisplatin) and included pneumonitis in 2 patients and hepatitis in 1 patient. KEYTRUDA was discontinued due to adverse reactions in 13% of 350 patients with locally advanced unresectable or metastatic HER2-positive gastric or GEJ adenocarcinoma. Adverse reactions resulting in permanent discontinuation of KEYTRUDA in ≥1% of patients were pneumonitis (2.0%) and pneumonia (1.1%). In the KEYTRUDA arm vs placebo, there was a difference of ≥5% incidence between patients treated with KEYTRUDA vs standard of care for diarrhea (53% vs 47%), rash (35% vs 28%), hypothyroidism (11% vs 5%), and pneumonia (11% vs 5%).</p>
                                <p>The most common adverse reactions (reported in ≥20%) in patients receiving KEYTRUDA in combination with chemotherapy or chemoradiotherapy were fatigue/asthenia, nausea, constipation, diarrhea, decreased appetite, rash, vomiting, cough, dyspnea, pyrexia, alopecia, peripheral neuropathy, mucosal inflammation, stomatitis, headache, weight loss, abdominal pain, arthralgia, myalgia, insomnia, palmar-plantar erythrodysesthesia, urinary tract infection, hypothyroidism, radiation skin injury, dysphagia, dry mouth, and musculoskeletal pain.</p>
                            </TextBlock>
                        </PageSection>
                    </TemplateColumn>
                    {/* End Main Column */}
                    
                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <CrossLink {...crossLinkContent} />
                        </PageSection>
                    </TemplateColumn>

                </TemplateColumnWithSidebarContainer>

                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent}/>
                    </PageSection>
                </TemplateColumn>
            </div>
        </DefaultLayout>
    );
};

export default Page;
